import NotificationsIcon from '@mui/icons-material/Notifications'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { NOTIFICATION_TYPE_LABELS, type NotificationDto } from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { qk } from '../../lib/queryClient'

type Feed = { items: NotificationDto[]; unread: number }

export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.get<Feed>('/notifications', { limit: 30 }),
    refetchInterval: 60_000,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.notifications }),
  })

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.notifications }),
  })

  const items = data?.items ?? []

  const openNotification = (notification: NotificationDto) => {
    if (!notification.readAt) markRead.mutate(notification.id)
    setAnchor(null)
    if (notification.taskId) navigate(`/tasks?taskId=${notification.taskId}`)
  }

  return (
    <>
      <Tooltip title="התראות">
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={data?.unread ?? 0} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 460 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">התראות</Typography>
          <Button
            size="small"
            onClick={() => markAllRead.mutate()}
            disabled={!data?.unread || markAllRead.isPending}
          >
            סמן הכל כנקרא
          </Button>
        </Box>
        <Divider />

        {items.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
            אין התראות
          </Typography>
        ) : (
          items.map((notification) => (
            <ListItemButton
              key={notification.id}
              onClick={() => openNotification(notification)}
              sx={{ bgcolor: notification.readAt ? 'transparent' : 'action.hover' }}
            >
              <ListItemText
                primary={NOTIFICATION_TYPE_LABELS[notification.type]}
                secondary={
                  <>
                    {notification.taskName ?? 'משימה שנמחקה'}
                    {' · '}
                    {formatDateTime(notification.createdAt)}
                  </>
                }
              />
            </ListItemButton>
          ))
        )}
      </Popover>
    </>
  )
}
